import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Sparkles, Loader2 } from "lucide-react";

// Sign-in gate branding is now server-resolved via /api/branding/by-host,
// which knows PRIVATE_LABEL_ROOT + PRIMARY_HOST. Frontend just passes the
// current window.location.hostname and gets back {mode, firm_name?, logos?, …}.
// A `?firm=acme` query param short-circuits to a direct firm lookup for
// preview environments where the host doesn't match the real root.

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  // Sign-in gate brand mode. One of:
  //   null      → still loading — show nothing to avoid flashing wrong brand
  //   "platform"→ SmartBooks brand (default; `app.smartbookssoftware.ai`)
  //   "neutral" → bare private-label root or unknown subdomain
  //   "firm"    → a specific firm's white-label brand (logos + colors)
  // When "firm", `firm` state holds { firm_name, logos, theme_preset, theme_custom }.
  const [mode, setMode] = useState(null);
  const [firm, setFirm] = useState(null);

  useEffect(() => {
    // Fix (Feb 2026): URL host is now the SOURCE OF TRUTH for
    // branding. Old logic let cached `axiom_firm_slug` in
    // localStorage win when the backend said "platform" — which
    // meant landing on `app.smartbookssoftware.ai/login` after a
    // prior visit to `scarlett.accountingapp.ai` still showed
    // ScarlettBooks branding (wrong). It also meant landing directly
    // on `scarlett.accountingapp.ai/login` showed platform default
    // when the backend didn't know that subdomain (also wrong).
    //
    // New priority chain:
    //   1. `?firm=<slug>` explicit override — for previews / dev.
    //   2. Subdomain from `window.location.hostname` — if the host
    //      looks like `<slug>.accountingapp.ai` (or any 2+ label
    //      pattern that isn't a known flagship / preview host), the
    //      leftmost slug wins. Ignores cached state entirely.
    //   3. `/branding/by-host` server-side resolve — for the flagship
    //      host itself and any labels the backend knows.
    //   4. Cached slug fallback — only when EVERYTHING else says
    //      "platform" AND we're not on a flagship / preview host.
    //   5. Platform default.
    //
    // On flagship / preview hosts we ALSO wipe the cached slug so a
    // stale session from a prior firm doesn't linger.

    // Hosts where we should NEVER show a firm brand — the platform
    // itself, previews, and localhost. Anything else that reaches
    // this branch is a candidate for subdomain-derived branding.
    const host = window.location.hostname.toLowerCase();
    const isFlagshipHost =
      host === "app.smartbookssoftware.ai" ||
      host === "smartbookssoftware.ai" ||
      host === "www.smartbookssoftware.ai" ||
      host === "localhost" ||
      host === "127.0.0.1" ||
      host.endsWith(".preview.emergentagent.com") ||
      host.endsWith(".emergentagent.com");

    if (isFlagshipHost) {
      // Kill any lingering firm-slug cache from a prior label
      // session so this host stays platform-branded.
      try { localStorage.removeItem("axiom_firm_slug"); } catch { /* ignore */ }
    }

    // 1. `?firm=<slug>` explicit override.
    const q = new URLSearchParams(window.location.search).get("firm");
    if (q) {
      api.get(`/branding/by-subdomain/${encodeURIComponent(q.toLowerCase().trim())}`)
        .then(r => {
          setFirm(r.data);
          setMode("firm");
          try { localStorage.setItem("axiom_firm_slug", q); } catch { /* ignore */ }
        })
        .catch(() => setMode("platform"));
      return;
    }

    // 2. Subdomain-derived slug from the actual URL host. Any host
    //    with 2+ dots that ISN'T flagship qualifies — first label is
    //    the candidate slug (`scarlett.accountingapp.ai` → `scarlett`).
    if (!isFlagshipHost) {
      const parts = host.split(".");
      if (parts.length >= 2) {
        const slug = parts[0];
        // Filter obvious non-brand labels.
        if (!["api", "www", "app", "admin", "preview"].includes(slug)) {
          api.get(`/branding/by-subdomain/${encodeURIComponent(slug)}`)
            .then(r => {
              setFirm(r.data);
              setMode("firm");
              try { localStorage.setItem("axiom_firm_slug", slug); } catch { /* ignore */ }
            })
            .catch(() => {
              // Unknown subdomain — try the server-side resolver,
              // then fall through to platform.
              serverResolve();
            });
          return;
        }
      }
    }

    // 3-5. Fallback to server-side host resolver + cached slug.
    serverResolve();

    function serverResolve() {
      api.get(`/branding/by-host?host=${encodeURIComponent(window.location.hostname)}`)
        .then(r => {
          const m = r.data?.mode || "platform";
          // Cached-slug fallback — but ONLY on non-flagship hosts.
          // Flagship must ALWAYS render platform branding regardless
          // of prior sessions.
          if (m === "platform" && !isFlagshipHost) {
            const cached = (() => { try { return localStorage.getItem("axiom_firm_slug"); } catch { return null; } })();
            if (cached) {
              api.get(`/branding/by-subdomain/${encodeURIComponent(cached)}`)
                .then(fr => { setFirm(fr.data); setMode("firm"); })
                .catch(() => setMode("platform"));
              return;
            }
          }
          setMode(m);
          if (m === "firm") setFirm(r.data);
        })
        .catch(() => setMode("platform"));
    }
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      nav(
        u.role === "superadmin" ? "/admin"
        : u.role === "practice" ? "/practice"
        : u.role === "underwriter" ? "/admin/merchant-review"
        : u.role === "partner" ? "/partner"
        : u.role === "pro" ? "/cockpit"
        : u.role === "client" ? "/owner"
        : (u.default_landing || "/dashboard")
      );
    } catch (e) {
      setErr(
        // 429 lockout returns `detail: {message, retry_after_seconds}` — surface the human copy.
        e.response?.data?.detail?.message
        || (typeof e.response?.data?.detail === "string" ? e.response.data.detail : null)
        || "Login failed",
      );
    } finally { setBusy(false); }
  };

  const demo = async (e, p, testid) => {
    setEmail(e); setPassword(p);
    setErr(""); setBusy(true);
    try {
      const u = await login(e, p);
      nav(
        u.role === "superadmin" ? "/admin"
        : u.role === "practice" ? "/practice"
        : u.role === "underwriter" ? "/admin/merchant-review"
        : u.role === "partner" ? "/partner"
        : u.role === "pro" ? "/cockpit"
        : u.role === "client" ? "/owner"
        : (u.default_landing || "/dashboard")
      );
    } catch (err) {
      // Surface the actual server response instead of a generic
      // "Demo login failed". On production, the most likely causes are
      //   * 401 "Invalid credentials" — demo users not seeded on that DB
      //   * 429 rate lockout — the user tapped a demo button 5+ times
      //   * Network / CORS
      // Every one of those needs a different fix; swallowing them made
      // production debugging impossible.
      const detail = err?.response?.data?.detail;
      const msg =
        (detail && detail.message) ||
        (typeof detail === "string" ? detail : null) ||
        err?.message ||
        "Demo login failed";
      setErr(`Demo login failed — ${msg}`);
    }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full flex bg-[#F5F6F8]">
      {/* Left marketing panel — SmartBooks brand shows the platform hero;
          firms that have set a custom `signin_hero_image` show it as a
          cover image (so their private-label sign-in has visual weight
          without the SmartBooks copy). Neutral hosts render nothing. */}
      {mode === "platform" && (
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 text-white p-12">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "radial-gradient(circle at 30% 20%, white 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }} />
        <div className="relative z-10 flex flex-col justify-between max-w-xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <Sparkles size={16} className="text-white" />
            </div>
            <div className="font-heading font-bold text-xl">SmartBooks</div>
          </div>
          <div className="space-y-6">
            <h1 className="font-heading text-5xl leading-[1.05] font-bold">
              The AI accountant<br/>your firm has been<br/>waiting for.
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed max-w-md">
              GAAP-based categorization, auto-posted JEs, split &amp; linked transactions,
              and real CPA-grade reports — all before your morning coffee.
            </p>
            <div className="grid grid-cols-3 gap-3 max-w-md">
              {[
                ["Auto-posted", "JEs & GLs"],
                ["Rules from", "3 approvals"],
                ["PDF-ready", "reports"],
              ].map(([a, b]) => (
                <div key={a} className="rounded-lg bg-white/5 border border-white/10 px-3 py-2.5">
                  <div className="font-heading font-semibold text-sm">{a}</div>
                  <div className="text-xs text-slate-400">{b}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-slate-400">© SmartBooks, Inc.</div>
        </div>
      </div>
      )}
      {mode === "firm" && firm?.signin_hero_image && (
        <div
          className="hidden lg:block flex-1 bg-slate-100 bg-cover bg-center"
          style={{ backgroundImage: `url(${firm.signin_hero_image})` }}
          data-testid="login-firm-hero-image"
        />
      )}

      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          {/* Firm white-label header — logo big and centered, firm name under it. */}
          {mode === "firm" && firm && (
            <div className="flex flex-col items-center text-center gap-3 mb-6" data-testid="login-firm-branding">
              {(firm.logos?.logo_light || firm.logos?.icon_light) ? (
                <img
                  src={firm.logos.logo_light || firm.logos.icon_light}
                  alt={firm.firm_name}
                  className="h-20 max-h-24 max-w-[320px] object-contain"
                />
              ) : (
                <div className="w-20 h-20 rounded-lg bg-blue-600 flex items-center justify-center">
                  <Sparkles size={36} className="text-white" />
                </div>
              )}
              <div className="font-heading font-bold text-lg text-slate-900">{firm.firm_name}</div>
              {/* No "Powered by" — firm's clients get a fully white-labeled
                  experience. Firms that want platform attribution can turn
                  it on in Enterprise Settings (future flag). */}
            </div>
          )}
          {/* SmartBooks header on mobile (mirrors the left marketing panel) */}
          {mode === "platform" && (
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <Sparkles size={16} className="text-white" />
              </div>
              <div className="font-heading font-bold">SmartBooks</div>
            </div>
          )}
          {/* Neutral (bare root or unknown subdomain) — deliberately empty:
              no logo, no brand. Just the sign-in form. */}
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Sign in</h2>
            <p className="text-sm text-slate-500 mt-1">
              {(mode === "firm" && firm?.signin_tagline) || "Welcome back. Let's get to the numbers."}
            </p>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Email</label>
              <input
                data-testid={TID.loginEmail}
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-500"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Password</label>
              <input
                data-testid={TID.loginPassword}
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-500"
                required
              />
            </div>
          </div>
          {err && <div data-testid={TID.loginError} className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
          <button
            data-testid={TID.loginSubmit}
            type="submit" disabled={busy}
            className="w-full h-10 rounded-md bg-slate-900 text-white font-medium text-sm flex items-center justify-center gap-2 hover:bg-slate-800 disabled:opacity-60"
          >
            {busy && <Loader2 size={14} className="animate-spin" />} Sign in
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setForgotOpen(true)}
              data-testid="forgot-password-link"
              className="text-xs text-slate-500 hover:text-cyan-700 hover:underline"
            >
              Forgot password?
            </button>
          </div>

          {/* Hide the "Create one" signup link per-tenant. Firms that
              onboard clients by invite only can flip this in Enterprise
              Settings so end-users don't see a public signup path. */}
          {!(mode === "firm" && firm?.hide_signup_link) && (
          <div className="text-center text-xs text-slate-500 space-y-1">
            <div>
              No account? <a href="/signup" className="text-cyan-700 hover:underline" data-testid="signup-link">Create one</a>
            </div>
            <div>
              Running a firm? <a href="/signup/enterprise" className="text-indigo-700 hover:underline" data-testid="signup-enterprise-link">Start on the enterprise plan</a>
            </div>
            <div>
              Just want to refer? <a href="/signup/affiliate" className="text-emerald-700 hover:underline" data-testid="signup-affiliate-link">Become an affiliate</a>
            </div>
          </div>
          )}

          {/* Hide the Demo Accounts block per-tenant. The `hide_demo_accounts`
              flag comes from the resolved firm branding (superadmin/pro can
              toggle it from Enterprise Settings). Also hide entirely on the
              live SmartBooks flagship host (`app.smartbookssoftware.ai`) so
              real signups don't see demo shortcuts — but keep them visible
              on preview / localhost / white-label subdomains for testing. */}
          {(() => {
            const host = typeof window !== "undefined"
              ? window.location.hostname.toLowerCase() : "";
            const isProductionFlagship =
              host === "app.smartbookssoftware.ai" ||
              host === "smartbookssoftware.ai" ||
              host === "www.smartbookssoftware.ai";
            if (isProductionFlagship) return null;
            if (mode === "firm" && firm?.hide_demo_accounts) return null;
            return (
              <div className="pt-4 border-t space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Demo accounts</div>
                <button type="button" data-testid={TID.demoClient} onClick={() => demo("client@axiom.ai", "client123")}
                        className="w-full text-left rounded-md border px-3 py-2 text-sm hover:bg-slate-50">
                  <span className="font-medium">Client</span> — Skyward Sparks, LLC
                </button>
                <button type="button" data-testid={TID.demoPro} onClick={() => demo("pro@axiom.ai", "pro123")}
                        className="w-full text-left rounded-md border px-3 py-2 text-sm hover:bg-slate-50">
                  <span className="font-medium">Accounting Pro</span> — Northgate Advisory
                </button>
                <button type="button" data-testid={TID.demoAdmin} onClick={() => demo("admin@axiom.ai", "admin123")}
                        className="w-full text-left rounded-md border px-3 py-2 text-sm hover:bg-slate-50">
                  <span className="font-medium">Superadmin</span> — Platform
                </button>
                <button type="button" data-testid={TID.demoPartner} onClick={() => demo("partner@axiom.ai", "partner123")}
                        className="w-full text-left rounded-md border px-3 py-2 text-sm hover:bg-slate-50">
                  <span className="font-medium">Partner</span> — AxiomPartners
                </button>
              </div>
            );
          })()}
        </form>
      </div>
      {forgotOpen && <ForgotPasswordModal onClose={() => setForgotOpen(false)} initialEmail={email} />}
    </div>
  );
}

function ForgotPasswordModal({ onClose, initialEmail }) {
  const [email, setEmail] = useState(initialEmail || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      // Always show success regardless of whether the email exists —
      // matches the backend's anti-enumeration behavior.
      setSent(true);
    } catch {
      setSent(true);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="forgot-password-modal"
      >
        {!sent ? (
          <form onSubmit={submit}>
            <div className="p-5 border-b">
              <h3 className="font-heading text-lg font-semibold text-slate-900">Reset your password</h3>
              <p className="text-xs text-slate-500 mt-1">
                Enter your email and we'll send you a link to set a new one. It'll be valid for 24 hours.
              </p>
            </div>
            <div className="p-5">
              <label className="text-xs text-slate-600">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus required
                data-testid="forgot-password-email"
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-md border text-sm">Cancel</button>
              <button
                type="submit" disabled={busy || !email.trim()}
                data-testid="forgot-password-submit"
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-cyan-600 text-white text-sm hover:bg-cyan-700 disabled:opacity-50"
              >
                {busy && <Loader2 size={13} className="animate-spin" />} Send reset link
              </button>
            </div>
          </form>
        ) : (
          <div className="p-6 text-center" data-testid="forgot-password-sent">
            <div className="text-3xl">📬</div>
            <h3 className="font-heading text-lg font-semibold text-slate-900 mt-2">Check your inbox</h3>
            <p className="text-sm text-slate-600 mt-2">
              If <b>{email}</b> is registered, we've sent a reset link. It'll expire in 24 hours.
            </p>
            <p className="text-xs text-slate-400 mt-3">
              Didn't see it? Check your spam folder, or try a different email.
            </p>
            <button
              onClick={onClose}
              className="mt-5 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
            >
              Got it
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
